import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';
import moment from 'moment';

import {
  UTILITY_PARTNER_STATUS_PENDING_DEMO,
  UTILITY_PARTNER_STATUS_ONBOARDED,
  UTILITY_PARTNER_STATUS_ACTIVE,
  UTILITY_PARTNER_STATUS_CHURNED,
  UTILITY_PARTNER_STATUS_ARCHIVED,
} from 'common/utility/constants';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';

import { SR_API_ACTION_UTILITY_PARTNER_UPDATE_STATUS } from 'api/utilityActionTypes';

import SrCard, {
  SR_CARD_BULLET_COLOR_BLUE,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import SrBox from 'client/components/SrBox';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrButton from 'client/components/SrButton/SrButton';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const STATUS_OPTIONS = [
  { label: 'Pending Demo', value: UTILITY_PARTNER_STATUS_PENDING_DEMO },
  { label: 'Onboarded', value: UTILITY_PARTNER_STATUS_ONBOARDED },
  { label: 'Active', value: UTILITY_PARTNER_STATUS_ACTIVE },
  { label: 'Churned', value: UTILITY_PARTNER_STATUS_CHURNED },
  { label: 'Archived', value: UTILITY_PARTNER_STATUS_ARCHIVED },
];

const getFormFields = ({ isAdmin, currentStatus }) => {
  const availableOptions = [
    { label: 'Churned', value: UTILITY_PARTNER_STATUS_CHURNED },
    { label: 'Archived', value: UTILITY_PARTNER_STATUS_ARCHIVED },
  ];

  if (
    currentStatus &&
    currentStatus !== UTILITY_PARTNER_STATUS_CHURNED &&
    currentStatus !== UTILITY_PARTNER_STATUS_ARCHIVED
  ) {
    const currentStatusOption = STATUS_OPTIONS.find((option) => option.value === currentStatus);
    if (currentStatusOption) {
      availableOptions.unshift(currentStatusOption);
    }
  }

  return [
    {
      id: 'status',
      title: 'Status',
      type: 'select',
      properties: {
        placeholder: 'Select a status',
        choices: availableOptions,
        disabled: !isAdmin,
      },
      validations: ['required'],
    },
  ];
};

const getChurnFields = ({ isAdmin }) => {
  return [
    {
      id: 'churnDate',
      title: 'Churn Date',
      type: 'date',
      properties: {
        placeholder: 'Select churn date',
        disabled: !isAdmin,
      },
      validations: ['required'],
    },
    {
      id: 'churnReason',
      title: 'Churn Reason',
      type: 'long_text',
      properties: {
        placeholder: 'Enter reason for churning',
        disabled: !isAdmin,
      },
      validations: ['required'],
    },
  ];
};

class PartnerStatusUpdateCard extends Component {
  static propTypes = {
    utilityPartner: PropTypes.object.isRequired,
    isAdmin: PropTypes.bool.isRequired,
    dispatch: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
  };

  constructor(props) {
    super(props);
    this.state = {
      errorMessage: null,
    };
  }

  _onSubmitForm = (formValues, formikBag) => {
    const { utilityPartner, dispatch, onSuccess } = this.props;

    this.setState({ errorMessage: null });

    const payload = {
      partnerId: utilityPartner.id,
      status: formValues.status,
    };

    if (formValues.status === UTILITY_PARTNER_STATUS_CHURNED) {
      payload.churnDate = formValues.churnDate
        ? moment(formValues.churnDate).format('YYYY-MM-DD')
        : null;
      payload.churnReason = formValues.churnReason;
    }

    return new Promise((resolve, reject) => {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_PARTNER_UPDATE_STATUS,
        payload,
        onError: (error) => {
          this.setState({
            errorMessage: error.message,
          });
          formikBag.setSubmitting(false);
          reject(error);
        },
        onSuccess: () => {
          dispatch(UtilityPartnerActions.getBySlug, {
            slug: utilityPartner.slug,
            forceRefresh: true,
          })
            .then(() => {
              if (onSuccess) {
                onSuccess();
              }
              resolve();
            })
            .catch((error) => {
              this.setState({
                errorMessage: error.message || 'Failed to refresh partner data',
              });
              reject(error);
            })
            .finally(() => {
              formikBag.setSubmitting(false);
            });
        },
      });
    });
  };

  render() {
    const { utilityPartner, isAdmin } = this.props;
    const { errorMessage } = this.state;

    const formFields = getFormFields({ isAdmin, currentStatus: utilityPartner.status });

    return (
      <Formik
        enableReinitialize
        onSubmit={this._onSubmitForm}
        initialValues={createInitialValues({
          formFields: [
            ...getFormFields({ isAdmin, currentStatus: utilityPartner.status }),
            ...getChurnFields({ isAdmin }),
          ],
          defaultValues: {
            status: utilityPartner.status,
            churnDate: utilityPartner.churnDate || null,
            churnReason: utilityPartner.churnReason || '',
          },
        })}
        validate={(values) => {
          const fieldsToValidate =
            values.status === UTILITY_PARTNER_STATUS_CHURNED
              ? [...formFields, ...getChurnFields({ isAdmin })]
              : formFields;
          return createFormValidationFn(fieldsToValidate)(values);
        }}
        render={(formikBag) => {
          return (
            <Form>
              <SrCard
                color={SR_CARD_BULLET_COLOR_BLUE}
                contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                headerFlexDirection="column"
                header={
                  <SrBox display="flex" alignItems="center" columnGap="12px">
                    <span>Partner Status</span>
                    <SrLabel
                      borderColor="#152c5b"
                      borderWidth="1"
                      color="blueNavy"
                      fontSize="small"
                      fontWeight="500"
                      padding="4px 12px"
                      label="UP Admin"
                    />
                  </SrBox>
                }
                subheader="Update the status of this partner in the system."
              >
                <SrBox
                  display="flex"
                  alignItems="flex-end"
                  justifyContent="space-between"
                  columnGap="16px"
                >
                  <SrBox flex="1 0 auto" maxWidth={300}>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                        minWidth={300}
                        theme={SR_FIELD_THEME_ADOBE}
                      />
                    ))}
                    {formikBag.values.status === UTILITY_PARTNER_STATUS_CHURNED &&
                      getChurnFields({ isAdmin }).map((formField) => (
                        <SrFormikField
                          key={formField.id}
                          formField={formField}
                          formikBag={formikBag}
                          minWidth={300}
                          theme={SR_FIELD_THEME_ADOBE}
                        />
                      ))}
                  </SrBox>
                  {isAdmin && (
                    <SrBox>
                      <SrButton type="submit" disabled={formikBag.isSubmitting}>
                        Save
                      </SrButton>
                    </SrBox>
                  )}
                </SrBox>

                {errorMessage && (
                  <SrBox marginTop="20px">
                    <SrErrorMessage>{errorMessage}</SrErrorMessage>
                  </SrBox>
                )}
              </SrCard>
            </Form>
          );
        }}
      />
    );
  }
}

PartnerStatusUpdateCard.displayName = 'PartnerStatusUpdateCard';

export default PartnerStatusUpdateCard;
